import { useMemo, useState } from "react";

import type { Dayjs } from "dayjs";
import type { Preset } from "./scheduler-utils";
import dayjs from "@punchup/utils/dayjs";
import { generatePresets, getUserTimezone } from "./scheduler-utils";
import { QUIET_HOURS_START, QUIET_HOURS_END } from "../constants";

type Mode = "presets" | "custom";

interface UseSchedulerStateOptions {
  initialScheduledDate: string | null;
  quickPicksEnabled?: boolean;
}

export function useSchedulerState({
  initialScheduledDate,
  quickPicksEnabled = true,
}: UseSchedulerStateOptions) {
  const now = useMemo(() => dayjs(), []);

  const presets = useMemo(() => generatePresets(now), [now]);

  // Derive initial values from an existing scheduled date
  const initial = useMemo(() => {
    if (!initialScheduledDate) return null;
    return dayjs(initialScheduledDate);
  }, [initialScheduledDate]);

  const [mode, setMode] = useState<Mode>(
    initial ? "custom" : quickPicksEnabled ? "presets" : "custom"
  );
  const [selectedPresetIndex, setSelectedPresetIndex] = useState<number | null>(
    null
  );
  const [selectedDate, setSelectedDate] = useState<Dayjs>(
    initial ? initial.startOf("day") : now.startOf("day")
  );
  const defaultTime = useMemo(() => getNextSuggested(now), [now]);
  const [selectedHour, setSelectedHour] = useState<number>(
    initial ? initial.hour() : defaultTime.hour
  );
  const [selectedMin, setSelectedMin] = useState<number>(
    initial ? initial.minute() : defaultTime.min
  );

  // 14-day range for the mobile day strip
  const dayOptions = useMemo(() => {
    const days: Dayjs[] = [];
    for (let i = 0; i < 14; i++) {
      days.push(now.startOf("day").add(i, "day"));
    }
    return days;
  }, [now]);

  const [selectedDayIndex, setSelectedDayIndex] = useState<number>(() => {
    if (!initial) return 0;
    const idx = dayOptions.findIndex((d) => d.isSame(initial, "day"));
    return idx >= 0 ? idx : -1;
  });

  const userTimezone = useMemo(() => getUserTimezone(), []);

  const [showCalendar, setShowCalendar] = useState(false);

  function toggleCalendar() {
    setShowCalendar((prev) => !prev);
  }

  // Resolve the currently selected date/time as a Dayjs
  // Returns null when "Immediately" preset is selected (date is null)
  function getScheduledDayjs(): Dayjs | null {
    if (mode === "presets") {
      if (selectedPresetIndex !== null) {
        return presets[selectedPresetIndex].date;
      }
      return null;
    }
    // custom mode
    return selectedDate.hour(selectedHour).minute(selectedMin).second(0);
  }

  // True when the "Immediately" preset is selected (index 0, null date)
  const isImmediateSelected =
    mode === "presets" &&
    selectedPresetIndex === 0 &&
    presets[0]?.date === null;

  function selectPreset(index: number) {
    setMode("presets");
    setSelectedPresetIndex(index);
    const preset = presets[index];
    // Skip syncing custom fields for "Immediately" (null date)
    if (preset.date) {
      setSelectedDate(preset.date.startOf("day"));
      setSelectedHour(preset.date.hour());
      setSelectedMin(preset.date.minute());
      const dayIdx = dayOptions.findIndex((d) => d.isSame(preset.date, "day"));
      if (dayIdx >= 0) setSelectedDayIndex(dayIdx);
    }
  }

  function selectDay(index: number) {
    setSelectedDayIndex(index);
    setSelectedDate(dayOptions[index]);
    setSelectedPresetIndex(null);
    setShowCalendar(false);
  }

  function selectTime(h: number, m: number) {
    setSelectedHour(h);
    setSelectedMin(m);
    setSelectedPresetIndex(null);
  }

  function selectCalendarDate(date: Date) {
    const d = dayjs(date).startOf("day");
    setSelectedDate(d);
    setSelectedPresetIndex(null);
    setShowCalendar(false);
    // Sync day strip index (-1 if outside the 14-day strip range)
    const dayIdx = dayOptions.findIndex((opt) => opt.isSame(d, "day"));
    setSelectedDayIndex(dayIdx);
  }

  function switchMode(m: Mode) {
    setMode(m);
    if (m === "custom") {
      setSelectedPresetIndex(null);
    }
  }

  function isPresetActive(preset: Preset): boolean {
    const idx = presets.indexOf(preset);

    // When a preset is explicitly selected, only that one is active
    if (selectedPresetIndex !== null) {
      return selectedPresetIndex === idx;
    }

    // In custom mode, highlight if date/time happens to match a non-immediate preset
    if (preset.date === null) return false;
    return (
      selectedDate.isSame(preset.date, "day") &&
      selectedHour === preset.date.hour() &&
      selectedMin === preset.date.minute()
    );
  }

  return {
    now,
    mode,
    switchMode,
    presets,
    selectedPresetIndex,
    selectPreset,
    selectedDate,
    selectedHour,
    selectedMin,
    selectedDayIndex,
    dayOptions,
    selectDay,
    selectTime,
    selectCalendarDate,
    getScheduledDayjs,
    isImmediateSelected,
    isPresetActive,
    showCalendar,
    toggleCalendar,
    userTimezone,
  };
}

/**
 * Suggest a default time rounded to 15-min boundaries, respecting quiet hours (8 AM – 9 PM).
 */
function getNextSuggested(now: Dayjs): { hour: number; min: number } {
  let suggested = now.add(1, "hour");

  // Round up to the next 15-min boundary
  const minutes = suggested.minute();
  const remainder = minutes % 15;
  if (remainder > 0) {
    suggested = suggested.add(15 - remainder, "minute").second(0);
  } else {
    suggested = suggested.second(0);
  }

  const hour = suggested.hour();
  const min = suggested.minute();

  if (hour < QUIET_HOURS_START) return { hour: QUIET_HOURS_START, min: 0 };
  if (hour >= QUIET_HOURS_END) return { hour: QUIET_HOURS_START, min: 0 };
  return { hour, min };
}
